import { useGetLabStatusTrackerQuery } from "@/services/dashboard";
import { useState, useMemo } from "react";
import { CHART_OPTIONS } from "./constants";

const useLabStatusTracker = () => {
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined,
    to: undefined,
  });

  const [selectedValue, setSelectedValue] = useState<string>("");


  const formatDate = (date: Date | undefined) => {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Validate date range - don't call API if start date is after end date
  const isDateRangeValid = useMemo(() => {
    if (dateRange.from && dateRange.to) {
      return dateRange.from <= dateRange.to;
    }
    return true; // Allow partial dates or undefined
  }, [dateRange.from, dateRange.to]);

  // Only include parameters in query when they are valid
  const queryParams = useMemo(() => {
    if (!isDateRangeValid) {
      // Return undefined when date range is invalid to prevent API call
      return undefined;
    }

    const params: {
      fromTargetDate?: string;
      endTargetDate?: string;
      region?: string;
    } = {};

    // Only add date params if both are provided and valid
    if (dateRange.from && dateRange.to) {
      params.fromTargetDate = formatDate(dateRange.from);
      params.endTargetDate = formatDate(dateRange.to);
    }

    // Only add region if it has a value (pass the ID directly)
    if (selectedValue) {
      params.region = selectedValue;
    }

    // Return params even if empty (for initial load)
    return params;
  }, [dateRange.from, dateRange.to, selectedValue, isDateRangeValid]);

  // Only enable query when date range is valid
  const shouldEnableQuery = isDateRangeValid && queryParams !== undefined;

  const { data, error, isLoading } = useGetLabStatusTrackerQuery(
    queryParams,
    {
      enabled: shouldEnableQuery,
      // Use placeholderData instead of keepPreviousData in v4
      placeholderData: (previousData) => previousData,
    }
  );

  const items = data?.data?.items || [];

  return {
    collectionsSummary: items,
    chartData: {
      labels: items.map((item) => item.status),
      datasets: [
        {
          label: "Lab Status",
          data: items.map((item) => item.count),
          backgroundColor: ["#FFB703", "#219EBC", "#FB8500", "#8ECAE6", "#023047"],
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    },
    chartOptions: CHART_OPTIONS,
    isLoading: isLoading && shouldEnableQuery,
    error,
    dateRange,
    setDateRange,
    selectedValue,
    setSelectedValue,
    isDateRangeValid,
  };
};

export default useLabStatusTracker;